import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Un deploy sostituisce i chunk con hash nel nome. Una scheda rimasta aperta a lungo ha ancora in
 * memoria il vecchio `index.html`: al primo click su una rotta lazy (oggi solo `/console`) prova a
 * scaricare un chunk che sul server non esiste piu' e l'import dinamico va in errore
 * ("Failed to fetch dynamically imported module"). La navigazione non parte, quindi nel sidebar
 * `selectedProduct`/`accordionExpanded` non vengono aggiornati e il submenu non si apre.
 *
 * L'unico recupero possibile e' ricaricare la pagina per prendere il nuovo `index.html`. Il reload
 * ha un budget (MAX_RELOADS in RELOAD_WINDOW_MS) per non entrare in loop se il chunk manca davvero
 * per un problema di build/deploy invece che per una versione stale.
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome/Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'chunkloaderror',
  'loading chunk',
  'unable to preload css'
];

const RELOAD_GUARD_KEY = 'tnc.chunkReloadGuard';
const RELOAD_WINDOW_MS = 60_000;
const MAX_RELOADS = 2;

@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.consumeReloadBudget()) {
      console.warn("[chunk-load] chunk non disponibile (deploy piu' recente): reload della pagina");
      window.location.reload();
      return;
    }
    console.error(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = this.collectMessages(error).toLowerCase();
    if (!message) return false;
    return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
  }

  /** Il router incapsula l'errore dell'import in `rejection`/`cause`, quindi va guardato in profondita'. */
  private collectMessages(error: unknown, depth = 0): string {
    if (error == null || depth > 4) return '';
    if (typeof error === 'string') return error;
    const parts: string[] = [];
    const candidate = error as { message?: unknown; name?: unknown; rejection?: unknown; cause?: unknown };
    if (typeof candidate.message === 'string') parts.push(candidate.message);
    if (typeof candidate.name === 'string') parts.push(candidate.name);
    parts.push(this.collectMessages(candidate.rejection, depth + 1));
    parts.push(this.collectMessages(candidate.cause, depth + 1));
    return parts.filter(Boolean).join(' | ');
  }

  private consumeReloadBudget(): boolean {
    const now = Date.now();
    let count = 0;
    try {
      const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { at?: number; count?: number };
        if (typeof parsed.at === 'number' && now - parsed.at < RELOAD_WINDOW_MS) {
          count = typeof parsed.count === 'number' ? parsed.count : 0;
        }
      }
      if (count >= MAX_RELOADS) return false;
      sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({ at: now, count: count + 1 }));
      return true;
    } catch {
      // sessionStorage non disponibile (private mode / storage bloccato): senza guardia non si
      // ricarica, meglio l'errore in console di un loop di reload.
      return false;
    }
  }
}
